import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * ErrorBoundary — catches unhandled React render errors and shows an
 * executive-grade fallback instead of a white screen.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Report to server for the audit trail. Best-effort — never rethrow.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      fetch(`${backend}/api/client-errors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: String(error?.message || error),
          stack: String(error?.stack || ""),
          component_stack: String(info?.componentStack || ""),
          url: typeof window !== "undefined" ? window.location.href : "",
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      }).catch(() => {});
    } catch { /* no-op */ }
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-8" data-testid="error-boundary">
        <div className="max-w-2xl w-full">
          <div className="flex items-center gap-3 text-[#A39171]">
            <AlertTriangle size={18} strokeWidth={1.4} />
            <div className="text-[11px] uppercase tracking-[0.28em]">Something went wrong</div>
          </div>
          <h1 className="serif text-4xl sm:text-5xl mt-6 leading-tight">
            We're on it.
          </h1>
          <p className="mt-6 text-white/70 max-w-lg leading-relaxed">
            The Travel + Leisure Supplier Engagement Platform hit an unexpected error while rendering this page.
            Our team has been notified. You can safely refresh — your data is not affected.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <button
              onClick={this.reset}
              data-testid="error-refresh-btn"
              className="inline-flex items-center gap-2 h-11 px-6 bg-white text-black text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#A39171] hover:text-white transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <a
              href="/"
              className="inline-flex items-center h-11 px-6 border border-[#A39171]/60 text-white text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-[#A39171] hover:text-black transition-colors"
            >
              Return Home
            </a>
          </div>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre className="mt-8 p-4 bg-black/60 border border-white/10 text-xs text-white/60 overflow-auto max-h-64 rounded">
              {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
